// @file Vuex store module for all things related to browser
import {
  browserCan,
  isDarkModeEnabled,
  os,
  visualViewportHeight,
  visualViewportScale,
  visualViewportWidth,
  windowHeight,
  windowWidth,
} from 'bits/browser'
import { isFullscreen } from 'bits/fullscreen'
import device from 'bits/device'

interface BrowserState {
  isFullscreen: boolean
  windowWidth: number
  windowHeight: number
  darkMode: boolean
  os: boolean | string
  visualViewportWidth: number
  visualViewportHeight: number
  visualViewportScale: number
}

// @see https://whimsical.com/sizes-AE8Cze2sDvD2eE56Q7eUo3
enum BreakPoints {
  Small = 744,
}

// Should be able to initialize with this in a context that doesn't have a full browser,
// e.g. for tests.
const blankState = (): BrowserState => ({
  isFullscreen: false,
  windowWidth: 1366,
  windowHeight: 768,
  darkMode: false,
  os: false,
  visualViewportWidth: 1366,
  visualViewportHeight: 768,
  visualViewportScale: 1,
})

const initialState = (): BrowserState => ({
  isFullscreen: isFullscreen(),
  windowWidth,
  windowHeight,
  darkMode: isDarkModeEnabled(),
  os: os(),
  visualViewportHeight,
  visualViewportWidth,
  visualViewportScale,
})

const browserModule = {
  namespaced: true,
  state: blankState,
  getters: {
    isFullscreen: (state): boolean => state.isFullscreen,
    windowWidth: (state): number => state.windowWidth,
    windowHeight: (state): number => state.windowHeight,
    isMobileView: (state): boolean => state.windowWidth < BreakPoints.Small,
    isSmallerThanTabletLandscape: (state): boolean => state.windowWidth <= 1024,
    isSmallerThanDesktop: (state): boolean => state.windowWidth <= 1173,
    // isPhone is the same as tailwindcss md breakpoint
    isPhone: (state): boolean => state.windowWidth < 768,
    isSmallPhone: (state): boolean => state.windowWidth <= 375,
    showMenuIcon: (state): boolean => state.windowWidth <= 800,
    isMobile: (): boolean => device.mobile,
    isTablet: (): boolean => device.tablet,
    isDesktop: (): boolean => device.desktop,
    visualViewportWidth: (state, getters): number =>
      browserCan('visualViewport') ? state.visualViewportWidth : getters.windowWidth,
    visualViewportHeight: (state, getters): number =>
      browserCan('visualViewport') ? state.visualViewportHeight : getters.windowHeight,
    visualViewportScale: (state): number => (browserCan('visualViewport') ? state.visualViewportScale : 1),
  },
  mutations: {
    INITIALIZE_STATE(state, data): void {
      Object.assign(state, data)
    },
    RESIZE_WINDOW(state, { width, height }): void {
      state.windowWidth = width
      state.windowHeight = height
    },
    ENTER_FULLSCREEN(state): void {
      state.isFullscreen = true
    },
    EXIT_FULLSCREEN(state): void {
      state.isFullscreen = false
    },
    TOGGLE_DARK_MODE(state, data): void {
      state.darkMode = data
    },
    RESIZE_VISUAL_VIEWPORT(state, { width, height, scale }: { width: number; height: number; scale: number }): void {
      state.visualViewportWidth = width
      state.visualViewportHeight = height
      state.visualViewportScale = scale
    },
  },
  actions: {
    initializeState({ commit }): void {
      commit('INITIALIZE_STATE', initialState())
    },
    resizeWindow({ commit }, data): void {
      const width = data.width
      const height = data.height
      commit('RESIZE_WINDOW', { width, height })
    },
    enterFullscreen({ commit }): void {
      commit('ENTER_FULLSCREEN')
    },
    exitFullscreen({ commit }): void {
      commit('EXIT_FULLSCREEN')
    },
    toggleDarkMode({ commit }, data): void {
      commit('TOGGLE_DARK_MODE', data)
    },
    resizeVisualViewport({ commit }, { width, height, scale }: { width: number; height: number; scale: number }): void {
      commit('RESIZE_VISUAL_VIEWPORT', { width, height, scale })
    },
  },
}

export default browserModule
export { BrowserState, BreakPoints, initialState }
